import { useEffect } from "react";
import { useLang } from "@/i18n/LanguageProvider";
import { ALL_LANGS, LANG_META } from "@/i18n/strings";
import { getI18n } from "@/i18n/site";

const SITE_URL = "https://freeways.kg/";
const OG_IMAGE = "https://freeways.kg/og-image.jpg";

function setMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function clearByDataKey(key: string) {
  document.head.querySelectorAll(`[data-seo="${key}"]`).forEach((n) => n.remove());
}

function appendLink(rel: string, href: string, extra?: Record<string, string>, key = "alt") {
  const link = document.createElement("link");
  link.setAttribute("rel", rel);
  link.setAttribute("href", href);
  if (extra) Object.entries(extra).forEach(([k, v]) => link.setAttribute(k, v));
  link.setAttribute("data-seo", key);
  document.head.appendChild(link);
}

function appendJsonLd(obj: unknown, key: string) {
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-seo", key);
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
}

export const SeoHead = () => {
  const { lang } = useLang();
  useEffect(() => {
    const t = getI18n(lang);
    const meta = LANG_META[lang];

    // Title + meta description
    document.title = t.seo.title;
    setMeta("meta[name='description']", "name", "description", t.seo.description);
    if (t.keywords) setMeta("meta[name='keywords']", "name", "keywords", t.keywords);

    // OG / Twitter
    setMeta("meta[property='og:title']", "property", "og:title", t.seo.title);
    setMeta("meta[property='og:description']", "property", "og:description", t.seo.description);
    setMeta("meta[property='og:locale']", "property", "og:locale", meta.ogLocale);
    setMeta("meta[property='og:url']", "property", "og:url", canonicalFor(lang));
    setMeta("meta[name='twitter:title']", "name", "twitter:title", t.seo.title);
    setMeta("meta[name='twitter:description']", "name", "twitter:description", t.seo.description);

    // Canonical
    let canon = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", canonicalFor(lang));

    // hreflang — per-language URLs (independent of canonical)
    clearByDataKey("hreflang");
    ALL_LANGS.forEach((l) => {
      const href = l === "ru" ? SITE_URL : `${SITE_URL}?lang=${l}`;
      appendLink("alternate", href, { hreflang: LANG_META[l].htmlLang }, "hreflang");
    });
    appendLink("alternate", SITE_URL, { hreflang: "x-default" }, "hreflang");

    // JSON-LD: AutoRental, FAQPage, WebSite
    clearByDataKey("jsonld");
    appendJsonLd({
      "@context": "https://schema.org",
      "@type": "AutoRental",
      name: "Freeways",
      url: SITE_URL,
      image: OG_IMAGE,
      description: t.seo.description,
      telephone: ["+996508810010", "+996778810010"],
      priceRange: "$$",
      currenciesAccepted: "USD, KGS, EUR",
      paymentAccepted: "Cash, Credit Card, QR, USDT",
      address: {
        "@type": "PostalAddress",
        streetAddress: "ул. Султана Ибраимова, 115/4",
        addressLocality: "Bishkek",
        addressCountry: "KG",
      },
      areaServed: ["Bishkek", "Kyrgyzstan", "Manas International Airport", "Issyk-Kul"],
      makesOffer: t.services.items.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.title, description: s.desc },
      })),
      sameAs: [
        "https://instagram.com/freeways.kg",
        "https://wa.me/996508810010",
      ],
    }, "jsonld");

    appendJsonLd({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Freeways",
      url: SITE_URL,
      inLanguage: meta.htmlLang,
    }, "jsonld");

    appendJsonLd({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: t.faq.items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    }, "jsonld");
  }, [lang]);
  return null;
};

function canonicalFor(lang: string) {
  // Canonical must match the corresponding hreflang URL for the current language
  // to avoid Lighthouse "Points to another hreflang location" error.
  return lang === "ru" ? SITE_URL : `${SITE_URL}?lang=${lang}`;
}

export default SeoHead;
